import React, { useEffect, useState } from "react";
import { Badge, Card, CardBody, Col } from "react-bootstrap";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { ErrorToast } from "../../shared/toasters/toasters";
import Pagination from "../../Common/Pagination";
import { AuditLogService, toAuditLogQueryParams } from "./service";
import { AuditLogEntry, AuditLogFilters as Filters, EMPTY_AUDIT_LOG_FILTERS } from "./types";
import { ACTION_BADGE_COLORS, EMPTY_VALUE } from "./format";
import AuditLogFilters from "./Filters";
import EntityTypeTabs from "./EntityTypeTabs";
import AuditLogDetailModal from "./DetailModal";

const entriesPerPageOptions = [
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
];

const AuditLogList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState<AuditLogEntry[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_AUDIT_LOG_FILTERS);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  const updateFilters = (changes: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...changes }));
    setCurrentPage(1);
  };

  const fetchData = async () => {
    try {
      const response = await AuditLogService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
        ...toAuditLogQueryParams(filters),
      });
      setList(response.data ?? []);
      setTotalPages(response.totalPages ?? 0);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage, filters]);

  const renderActor = (item: AuditLogEntry) => (
    <>
      <Badge bg="secondary" className="me-1">
        {t(`enums.auditActorType.${item.actorType}`, item.actorType)}
      </Badge>
      {item.actorEmail ?? (item.actorId ? `#${item.actorId}` : "")}
    </>
  );

  const renderChangedFields = (item: AuditLogEntry) =>
    item.changes ? Object.keys(item.changes).join(", ") : EMPTY_VALUE;

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <EntityTypeTabs
              value={filters.entityType}
              onChange={(entityType) => updateFilters({ entityType, entityId: "" })}
            />
            <div className="mb-3">
              <AuditLogFilters filters={filters} onChange={updateFilters} />
            </div>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div style={{ width: 100 }}>
                <Select
                  options={entriesPerPageOptions}
                  onChange={(option: any) => {
                    setEntriesPerPage(option ? option.value : 20);
                    setCurrentPage(1);
                  }}
                  defaultValue={entriesPerPageOptions.find((option) => option.value === entriesPerPage)}
                  classNamePrefix="react-select"
                />
              </div>
              <span>{t("auditLog.entriesPerPage")}</span>
            </div>
            <div className="table-responsive">
              <table className="table table-hover sticky-actions" id="pc-dt-audit-log-list">
                <thead>
                  <tr>
                    <th>{t("auditLog.createdAt")}</th>
                    <th>{t("auditLog.entity")}</th>
                    <th>{t("auditLog.action")}</th>
                    <th>{t("auditLog.actor")}</th>
                    <th>{t("auditLog.company")}</th>
                    <th>{t("auditLog.changedFields")}</th>
                    <th className="sticky-col">{t("auditLog.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => (
                    <tr key={item.id}>
                      <td className="text-nowrap">{new Date(item.createdAt).toLocaleString()}</td>
                      <td className="text-nowrap">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          title={t("auditLog.filterByEntity")}
                          onClick={() => updateFilters({ entityType: item.entityType, entityId: String(item.entityId) })}
                        >
                          {t(`enums.auditEntityType.${item.entityType}`, item.entityType)} #{item.entityId}
                        </button>
                      </td>
                      <td>
                        <Badge bg={ACTION_BADGE_COLORS[item.action] ?? "secondary"}>
                          {t(`enums.auditAction.${item.action}`, item.action)}
                        </Badge>
                      </td>
                      <td className="wrap-col">{renderActor(item)}</td>
                      <td>{item.companyId ? `#${item.companyId}` : EMPTY_VALUE}</td>
                      <td className="wrap-col">{renderChangedFields(item)}</td>
                      <td className="text-nowrap sticky-col">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => setSelectedEntry(item)}
                          title={t("auditLog.view")}
                        >
                          <i className="ti ti-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!list.length && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted">
                        {t("auditLog.empty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              previousLabel={t("auditLog.previous")}
              nextLabel={t("auditLog.next")}
            />
          </CardBody>
        </Card>
      </Col>
      <AuditLogDetailModal entry={selectedEntry} onHide={() => setSelectedEntry(null)} />
    </React.Fragment>
  );
};

export default AuditLogList;
